import * as React from "react";

/** The shell's right column is a slot a page fills through a portal
 *  (``ShellRightColumn`` is the host element AppShell renders). A page
 *  claims it while it has content for it; the column shows only while
 *  claimed, so pages without one get no empty panel. */
export type RightColumnSlot = {
  host: HTMLElement | null;
  claim: () => () => void;
};

export const RightColumnContext = React.createContext<RightColumnSlot | null>(null);

/** AppShell's side: the host element and whether any page claims it. */
export function useRightColumnSlot(): {
  slot: RightColumnSlot;
  setHost: (el: HTMLElement | null) => void;
  claimed: boolean;
} {
  const [host, setHost] = React.useState<HTMLElement | null>(null);
  const [claims, setClaims] = React.useState(0);
  const claim = React.useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => Math.max(0, n - 1));
  }, []);
  const slot = React.useMemo(() => ({ host, claim }), [host, claim]);
  return { slot, setHost, claimed: claims > 0 };
}

/** A page's side: while ``active``, claim the column and get the element
 *  to portal into. ``host`` is null outside the shell (tests, a page
 *  rendered on its own), while inactive, and on the shell's first commit
 *  (its host element is set in the same commit as the page's first
 *  render). ``inShell`` tells the last case apart: a page in the shell
 *  that is waiting for the host should render nothing there for a frame,
 *  not a fallback layout it would then tear down. */
export function useShellRightColumn(active: boolean): {
  host: HTMLElement | null;
  inShell: boolean;
} {
  const slot = React.useContext(RightColumnContext);
  const claim = slot?.claim;
  React.useEffect(() => {
    if (!claim || !active) return undefined;
    return claim();
  }, [claim, active]);
  return { host: active && slot ? slot.host : null, inShell: slot !== null };
}
